// useMe({ enabled? }) → UseQueryResult<Me>     key ["me"]
// Who is signed in (GET /v1/auth/me): { authenticated: false } or { authenticated: true, user }.
// Enable it only when config.auth.enabled. Re-checked whenever the window regains focus (a sign-in or
// sign-out in another tab) and every 60 s while the page is visible. The malbox_session cookie is
// HttpOnly and never read from JS: this endpoint is the only source of session state.
// useLogout() → mutation: POST /v1/auth/logout (204), then ["me"] reads signed out and is invalidated.
// loginHref(config?, returnTo?) → "/v1/auth/github/login?return=%2Fjobs%3Fq%3D…" (full-page navigation)
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AppConfig, Me } from "@/types";
import { keys } from "./keys";

const ME_POLL_MS = 60_000;

export function useMe(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => api.me(),
    staleTime: 30_000,
    refetchOnWindowFocus: "always", // the app default is off; a sign-in in another tab shows up here
    refetchInterval: ME_POLL_MS,
    enabled: opts.enabled ?? true,
    retry: 1,
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.logout(),
    onSuccess: () => {
      qc.setQueryData<Me>(keys.me, { authenticated: false });
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.me }),
  });
}

export function useUpdateDisplayName() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.updateMe(name),
    onSuccess: (me) => {
      qc.setQueryData<Me>(keys.me, me);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.me }),
  });
}

export function loginHref(config?: AppConfig, returnTo?: string): string {
  const back = returnTo ?? (typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : "/");
  return api.loginURL(back, config?.auth.github_login_url || undefined);
}
